import { FC, Fragment, useCallback, useMemo, useState } from "react"
import { IoClose } from "@superwall/icons/io5/IoClose"
import { Dialog, Transition } from "@headlessui/react"
import { OverlayProps } from "../types"

const SlideOverlay: FC<OverlayProps> = ({ onOpen, onClose, render, renderButton }) => {
  let [isOpen, setIsOpen] = useState(false)

  const open = useCallback(() => {
    setIsOpen(true)
    onOpen ? onOpen() : null
  }, [onOpen])

  const close = useCallback(() => {
    setIsOpen(false)
    onClose
      ? setTimeout(() => {
          onClose()
        }, 200)
      : null
  }, [onClose])

  const renderedButton = useMemo(() => renderButton({ open, close }), [renderButton, open, close])
  const rendered = useMemo(() => render({ open, close }), [render, open, close])
  return (
    <>
      {renderedButton}
      <Transition.Root show={isOpen} as={Fragment}>
        <Dialog as="div" className="fixed inset-0 overflow-hidden" onClose={close}>
          <div className="absolute inset-0 overflow-hidden">
            <Transition.Child
              as={Fragment}
              enter="ease-in-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in-out duration-300"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="absolute inset-0 bg-grayscale-darkest bg-opacity-80 transition-opacity" />
            </Transition.Child>
            <div className="fixed inset-y-0 left-0 pr-10 max-w-full flex">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-300"
                enterFrom="-translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-300"
                leaveFrom="translate-x-0"
                leaveTo="-translate-x-full"
              >
                <div className="relative w-screen max-w-sm xl:max-w-md">
                  <Transition.Child
                    as={Fragment}
                    enter="ease-in-out duration-300 origin-center-center"
                    enterFrom="opacity-0 scale-0 -translate-x-full"
                    enterTo="opacity-100 scale-100 translate-x-0"
                    leave="ease-in-out duration-300 origin-center-center"
                    leaveFrom="opacity-100 scale-100"
                    leaveTo="opacity-0 scale-0"
                  >
                    <div className="absolute top-0 opacity-50 right-0 translate-x-full -ml-8 p-4 flex">
                      <button
                        type="button"
                        className="rounded-md text-gray-300 hover:text-white"
                        onClick={close}
                      >
                        <span className="sr-only">Close panel</span>
                        <IoClose className="h-6 w-6" aria-hidden="true" />
                      </button>
                    </div>
                  </Transition.Child>

                  <div className="h-full flex flex-col py-6 bg-grayscale-dark border-r border-1 border-white border-opacity-7 shadow-xl overflow-y-scroll">
                    <div className="px-6">{rendered}</div>
                  </div>
                </div>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  )
}

export default SlideOverlay
